import React, { useCallback, useMemo } from "react";
import { Lock, Check, Loader } from "lucide-react";
import { Module, CourseProgress, ModuleProgress, Course } from "./types";
import { calculateModuleProgress } from "./utils";
import CourseCard from "./CourseCard";
import { useModuleCourses, areAllModuleProgressionsComplete } from "./api";

interface ModuleSectionProps {
  module: Module;
  moduleIndex: number;
  progressData: CourseProgress[];
  moduleProgressData: ModuleProgress[];
  selectedModuleId: string | null;
  handleModuleClick: (moduleId: string) => void;
  openVideo: (moduleTitle: string, course: Course) => void;
}

// Composant pour la barre de progression
const ProgressBar: React.FC<{
  percent: number;
  completed: number;
  total: number;
  isCompleted: boolean;
}> = ({ percent, completed, total, isCompleted }) => {
  // Utilisez les valeurs telles quelles, sans manipulation supplémentaire
  return (
    <div className="mb-4">
      <div className="flex flex-col">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">
            Progression du module
          </span>
          <span className="text-sm font-medium text-gray-700">
            {completed}/{total} capsules ({percent}%)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`${
              isCompleted ? "bg-green-500" : "bg-orange-500"
            } h-2.5 rounded-full`}
            style={{ width: `${percent}%` }}
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Composant pour l'indicateur de statut
const StatusBadge: React.FC<{ isCompleted: boolean; isEnabled: boolean }> = ({
  isCompleted,
  isEnabled,
}) => {
  if (isCompleted) {
    return (
      <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
        Module complété
      </span>
    );
  } else if (isEnabled) {
    return (
      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
        Module en cours
      </span>
    );
  } else {
    return (
      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
        Module verrouillé
      </span>
    );
  }
};

const ModuleSection: React.FC<ModuleSectionProps> = ({
  module,
  moduleIndex,
  progressData,
  moduleProgressData,
  selectedModuleId,
  handleModuleClick,
  openVideo,
}) => {
  const isSelected = selectedModuleId === module.id;
  const isEnabled = module.isEnabled !== false;

  // Détection améliorée des modules complétés
  const isCompleted = useMemo(() => {
    // Méthode 1: Vérifier les moduleProgressData (données explicites de progression de module)
    const explicitlyCompleted = moduleProgressData.some(
      (p) => p.moduleId === module.id && p.completed
    );

    if (explicitlyCompleted) {
      console.log(
        `Module ${module.id} est explicitement marqué comme complété via moduleProgressData`
      );
      return true;
    }

    // Méthode 2: Vérifier si toutes les progressions liées à ce module sont complètes
    const allProgressionsComplete = areAllModuleProgressionsComplete(
      module.id,
      progressData
    );

    if (allProgressionsComplete) {
      console.log(
        `Module ${module.id} est complété car toutes ses progressions sont complètes`
      );
      return true;
    }

    // Méthode 3: Si le module a des courses, vérifier si toutes sont complétées
    if (module.courses && module.courses.length > 0) {
      const allCoursesCompleted = module.courses.every((course) =>
        progressData.some((p) => p.courseId === course.id && p.completed)
      );

      if (allCoursesCompleted && module.courses.length > 0) {
        console.log(
          `Module ${module.id} est complété car tous ses cours sont complétés`
        );
        return true;
      }
    }

    return false;
  }, [module.id, module.courses, moduleProgressData, progressData]);

  // Toujours charger les cours du module (pas seulement quand sélectionné)
  const {
    courses: moduleCourses,
    loading: loadingCourses,
    error: coursesError,
  } = useModuleCourses(module.id);

  // Memorize expensive calculations
  const progress = useMemo(() => {
    // Ajout de logs de débogage pour identifier les problèmes de progression
    console.log(
      `Calcul de progression pour le module ${module.id} - ${module.title}:`
    );
    console.log(`- Cours du module:`, module.courses?.length || 0);
    console.log(`- Données de progression:`, progressData.length);
    console.log(`- Module courses (chargées):`, moduleCourses?.length || 0);

    // Si nous avons des cours chargés via useModuleCourses, les utiliser prioritairement
    // plutôt que les courses du module (qui peuvent être absentes)
    if (moduleCourses && moduleCourses.length > 0) {
      console.log(
        `Utilisation des moduleCourses pour le calcul de progression:`,
        moduleCourses.length
      );

      // Créons un module temporaire avec les cours chargés
      const tempModule = {
        ...module,
        courses: moduleCourses,
      };

      // Vérifions si tous les cours sont complétés
      const allCoursesCompleted = moduleCourses.every(
        (course) => course.completed === true
      );

      // Si tous les cours sont complétés, retourner 100%
      if (allCoursesCompleted) {
        console.log(
          `Tous les cours (${moduleCourses.length}) sont marqués comme complétés`
        );
        return {
          completed: moduleCourses.length,
          total: moduleCourses.length,
          percent: 100,
        };
      }

      // Filtrer les progressions liées à ce module
      const moduleProgressData = progressData.filter(
        (p) =>
          moduleCourses.some((c) => c.id === p.courseId) ||
          String(p.courseId).includes(module.id)
      );

      console.log(
        `Progressions filtrées avec moduleCourses:`,
        moduleProgressData.length
      );

      // Compter le nombre de cours complétés
      const completedCount = moduleCourses.filter(
        (course) => course.completed === true
      ).length;
      console.log(
        `Nombre de cours complétés selon leur propriété 'completed':`,
        completedCount
      );

      // Si nous n'avons pas de progressions mais les cours indiquent leur état de complétion
      if (moduleProgressData.length === 0 && completedCount > 0) {
        return {
          completed: completedCount,
          total: moduleCourses.length,
          percent: Math.round((completedCount / moduleCourses.length) * 100),
        };
      }

      return calculateModuleProgress(
        tempModule,
        moduleProgressData.length > 0 ? moduleProgressData : []
      );
    }

    // Si on est arrivé ici, c'est qu'on n'a pas de cours chargés
    // Continuer avec la méthode normale

    // Filtrer les progressions liées à ce module (si possible)
    const moduleProgressData = progressData.filter((p) => {
      // Essayer de déterminer si cette progression concerne ce module
      // Soit via les IDs des cours qui peuvent contenir l'ID du module
      return (
        module.courses?.some((c) => c.id === p.courseId) ||
        (p.courseId &&
          module.id &&
          String(p.courseId).includes(String(module.id)))
      );
    });

    console.log(
      `- Progressions filtrées pour ce module:`,
      moduleProgressData.length
    );

    // Si toutes les progressions filtrées sont complètes et qu'il y en a au moins une,
    // c'est probablement un module terminé
    if (
      moduleProgressData.length > 0 &&
      moduleProgressData.every((p) => p.completed)
    ) {
      console.log(
        `Module ${module.id} semble complété (toutes les progressions sont complétées)`
      );

      // Si nous n'avons pas de courses dans le module mais des progressions complètes
      if (!module.courses || module.courses.length === 0) {
        return {
          completed: moduleProgressData.length,
          total: moduleProgressData.length,
          percent: 100,
        };
      }
    }

    // Vérifier si le module est marqué comme complété
    if (isCompleted) {
      console.log(
        `Module ${module.id} est explicitement marqué comme complété`
      );
      // Nombre de capsules arbitraire pour un module complété mais sans données de progression
      const coursesCount = moduleCourses?.length || 3;
      return {
        completed: coursesCount,
        total: coursesCount,
        percent: 100,
      };
    }

    return calculateModuleProgress(
      module,
      moduleProgressData.length > 0 ? moduleProgressData : progressData
    );
  }, [module, progressData, moduleCourses, isCompleted]);

  // Utilisation de useCallback pour les gestionnaires d'événements
  const handleModuleClickCallback = useCallback(() => {
    if (isEnabled) {
      handleModuleClick(module.id);
    }
  }, [isEnabled, handleModuleClick, module.id]);

  // Render courses or loading state
  const renderCourseContent = () => {
    console.log(
      `renderCourseContent pour module ${module.id} (${module.title}):`
    );
    console.log(`- isSelected: ${isSelected}`);
    console.log(`- loadingCourses: ${loadingCourses}`);
    console.log(`- coursesError: ${!!coursesError}`);
    console.log(`- moduleCourses: ${moduleCourses?.length || 0}`);
    console.log(`- isCompleted: ${isCompleted}`);

    // Si le module est complété mais qu'on n'a pas encore chargé les cours
    if (isCompleted && moduleCourses?.length === 0 && !coursesError) {
      console.log(
        `Module ${module.id} marqué comme complété mais aucun cours n'est chargé`
      );
    }

    if (loadingCourses) {
      return (
        <div className="col-span-3 py-8 text-center">
          <Loader className="inline-block h-8 w-8 animate-spin text-orange-600" />
          <p className="mt-2 text-gray-600">Chargement des capsules...</p>
        </div>
      );
    }

    if (coursesError) {
      return (
        <div className="col-span-3 py-8 text-center">
          <p className="text-red-600 mb-2">
            Le chargement des capsules a échoué.
          </p>
          <p className="text-gray-600">
            Essayez de rafraîchir la page ou revenez plus tard.
          </p>
          {isCompleted && (
            <p className="mt-2 text-green-600">
              Ce module est cependant marqué comme complété.
            </p>
          )}
        </div>
      );
    }

    if (moduleCourses && moduleCourses.length > 0) {
      console.log("ModuleSection - courses pour le module:", module.title);
      console.log("ModuleSection - progressData:", progressData);
      console.log("ModuleSection - moduleCourses:", moduleCourses);

      return moduleCourses.map((course) => (
        <CourseCard
          key={`course-${course.id}`}
          course={course}
          moduleTitle={module.title}
          moduleIndex={moduleIndex}
          progressData={progressData}
          openVideo={openVideo}
        />
      ));
    } else {
      return (
        <div className="col-span-3 py-8 text-center">
          {isCompleted ? (
            <>
              <p className="text-green-600 mb-2">Module complété</p>
              <p className="text-gray-600">
                Toutes les capsules de ce module sont complétées.
              </p>
            </>
          ) : (
            <p className="text-gray-600">
              Aucune capsule disponible pour ce module
            </p>
          )}
        </div>
      );
    }
  };

  const moduleContainerClasses = `${moduleIndex > 0 ? "mt-16" : ""} ${
    !isEnabled && selectedModuleId ? "opacity-40 pointer-events-none" : ""
  } transition-opacity duration-300`;

  const moduleIconClasses = `${
    isEnabled ? "bg-gray-200" : "bg-gray-400"
  } rounded-full p-2 mr-4 relative cursor-pointer transition-all duration-300 ${
    isSelected ? "ring-2 ring-orange-500" : ""
  }`;

  const moduleContentClasses = `${
    isEnabled ? "bg-orange-100" : "bg-gray-200"
  } rounded-xl p-6 mb-8 transition-all duration-300 ${
    isSelected ? "ring-2 ring-orange-400 shadow-lg" : ""
  }`;

  return (
    <div className={moduleContainerClasses}>
      {/* Indicateur de progression du module - Toujours affiché si le module est accessible */}
      {isEnabled && (
        <ProgressBar
          percent={progress.percent}
          completed={progress.completed}
          total={progress.total}
          isCompleted={isCompleted}
        />
      )}

      {/* Section du titre avec l'icône */}
      <div className="flex items-center mb-6">
        <div
          className={moduleIconClasses}
          onClick={handleModuleClickCallback}
          role="button"
          aria-label={`Sélectionner le module ${module.title}`}
          tabIndex={isEnabled ? 0 : -1}
        >
          <img
            src="/images/armoirie-transp.png"
            alt="Icône du module"
            className="w-6 h-6"
          />
          {!isEnabled && (
            <div className="absolute -top-1 -right-1 bg-red-500 rounded-full p-1">
              <Lock size={12} className="text-white" aria-hidden="true" />
            </div>
          )}
          {isCompleted && (
            <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1">
              <Check size={12} className="text-white" aria-hidden="true" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <h2
            className={`text-xl font-bold uppercase ${
              !isEnabled ? "text-gray-500" : ""
            } cursor-pointer`}
            onClick={handleModuleClickCallback}
            role="button"
            aria-expanded={isSelected}
            aria-controls={`module-content-${module.id}`}
          >
            : {module.title}
          </h2>
          <div className="flex items-center mt-1">
            <StatusBadge isCompleted={isCompleted} isEnabled={isEnabled} />
          </div>
        </div>
      </div>

      <p className="mb-6 text-gray-700">{module.description}</p>

      {/* Carte conteneur pour les cours */}
      <div className={moduleContentClasses} id={`module-content-${module.id}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderCourseContent()}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ModuleSection);
